export const AGENT_STREAM_VERSION = "agent-v1" as const;

export type AgentEventType =
  | "response.started"
  | "stage.started"
  | "web_search.searching"
  | "web_search.completed"
  | "web_search.failed"
  | "text.delta"
  | "text.snapshot"
  | "citation.added"
  | "source.added"
  | "artifact.generating"
  | "artifact.ready"
  | "artifact.failed"
  | "response.deferred"
  | "response.completed"
  | "response.failed";

export type AgentRequestStatus =
  | "STARTING"
  | "RUNNING"
  | "WAITING_ARTIFACT"
  | "FINALIZING"
  | "COMPLETED"
  | "PARTIAL"
  | "FAILED"
  | "CANCELLED"
  | "OUTCOME_UNKNOWN";

export type AgentStage = "PLAN" | "SEARCH" | "ARTIFACT" | "FINAL";

export type AgentArtifactStatus =
  | "CREATED"
  | "ACCEPTED"
  | "GENERATING"
  | "READY"
  | "FAILED"
  | "EXPIRED"
  | "DELETED";

export interface AgentEvent<TPayload = Record<string, any>> {
  version: "1";
  requestId: string;
  sequence: number;
  type: AgentEventType;
  stage?: AgentStage;
  occurredAt?: string;
  textRevision?: number | null;
  payload: TPayload;
}

export interface AgentCitation {
  provider?: string;
  title?: string | null;
  url: string;
  startIndex?: number | null;
  endIndex?: number | null;
  indexValid?: boolean;
  webSearchCallId?: string;
  textRevision?: number | null;
  textSha256?: string | null;
  highlightable?: boolean;
}

export interface AgentSource {
  provider?: string;
  title?: string | null;
  url: string;
  webSearchCallId?: string;
}

export interface AgentArtifact {
  artifactId: string;
  filename?: string;
  /**
   * Renderer 返回的实际文件格式。它与 capabilities/请求使用的逻辑格式
   * AgentArtifactFormat 不同；保留开放字符串以兼容后端后续新增格式。
   */
  format?: AgentArtifactActualFormat;
  mimeType?: string;
  size?: number;
  sha256?: string;
  status: AgentArtifactStatus;
  statusPath?: string;
  downloadPath?: string;
  errorCode?: string;
  errorMessage?: string;
  message?: string;
  expireTime?: string;
}

export interface AgentMessageState {
  protocolVersion: typeof AGENT_STREAM_VERSION;
  requestId: string;
  status: AgentRequestStatus;
  currentStage?: AgentStage;
  deliveryMode?: "FOREGROUND" | "BACKGROUND";
  lastSequence: number;
  textSnapshot: string;
  paintingGuide?: boolean;
  textRevision?: number | null;
  textSha256?: string | null;
  citations: AgentCitation[];
  sources: AgentSource[];
  artifacts: AgentArtifact[];
  requestStatusPath?: string;
  retryAfterMs?: number;
  streamRecoverable?: boolean;
  terminal: boolean;
  deferred: boolean;
  searching: boolean;
  searchStarted?: boolean;
  searchCompleted?: boolean;
  searchWarningCode?: string;
  errorCode?: string;
  errorMessage?: string;
  partial?: boolean;
  updatedAt?: string;
}

export interface AgentRequestStatusResponse {
  requestId: string;
  status: AgentRequestStatus;
  deliveryMode?: "FOREGROUND" | "BACKGROUND";
  streamRecoverable?: boolean;
  textSnapshot?: string;
  paintingGuide?: boolean;
  textRevision?: number | null;
  textSha256?: string | null;
  snapshotSequence?: number;
  citations?: AgentCitation[];
  sources?: AgentSource[];
  searchExecuted?: boolean;
  searchSucceeded?: boolean;
  searchWarningCode?: string;
  artifacts?: AgentArtifact[];
  requestStatusPath?: string;
  retryAfterMs?: number;
  errorCode?: string;
  errorMessage?: string;
  message?: string;
  partial?: boolean;
  updatedAt?: string;
}

export interface AgentCapabilitiesResponse {
  enabled: boolean;
  artifactEnabled: boolean;
  streamVersion?: string;
  businessModels?: string[];
  artifactFormats?: AgentArtifactFormat[];
}

export type AgentArtifactIntent = "none" | "auto" | "required";
export type AgentArtifactFormat =
  | "auto"
  | "md"
  | "txt"
  | "csv"
  | "xlsx"
  | "docx"
  | "code";

export type KnownAgentArtifactActualFormat =
  | "md"
  | "txt"
  | "csv"
  | "xlsx"
  | "docx"
  | "java"
  | "py"
  | "js"
  | "ts"
  | "jsx"
  | "tsx"
  | "vue"
  | "sql"
  | "json"
  | "xml"
  | "html"
  | "css";

export type AgentArtifactActualFormat =
  | KnownAgentArtifactActualFormat
  | (string & {});

export const AGENT_TERMINAL_STATUSES: AgentRequestStatus[] = [
  "COMPLETED",
  "PARTIAL",
  "FAILED",
  "CANCELLED",
  "OUTCOME_UNKNOWN",
];

export const ARTIFACT_TERMINAL_STATUSES: AgentArtifactStatus[] = [
  "READY",
  "FAILED",
  "EXPIRED",
  "DELETED",
];
